// src/features/infoscreens/hooks/useInfoscreens.js
import { useState, useCallback } from 'react';
import { infoscreensAPI } from '../services/infoscreens';
import { useApi } from '../../../hooks/api/useApi';
import { SWITCH_SESSION_KEY } from '../../auth/services/authService';

export function useInfoscreens() {
  const [infoscreens, setInfoscreens] = useState([]);
  const [currentInfoscreen, setCurrentInfoscreen] = useState(null);
  const [infoscreenStatus, setInfoscreenStatus] = useState(null);
  const { loading, error, execute, clearError } = useApi();

  const fetchInfoscreens = useCallback(async () => {
    const result = await execute(() => infoscreensAPI.listInfoscreens());
    if (result.success) {
      setInfoscreens(result.data.infoscreens || []);
    }
    return result;
  }, [execute]);

  const fetchInfoscreenById = useCallback(async (infoscreenId) => {
    const result = await execute(() => infoscreensAPI.getInfoscreenById(infoscreenId));
    if (result.success) {
      setCurrentInfoscreen(result.data);
    }
    return result;
  }, [execute]);

  const createInfoscreen = useCallback(async (data) => {
    const result = await execute(() => infoscreensAPI.createInfoscreen(data));
    if (result.success) {
      await fetchInfoscreens();
    }
    return result;
  }, [execute, fetchInfoscreens]);

  const updateInfoscreen = useCallback(async (infoscreenId, data) => {
    const result = await execute(() => infoscreensAPI.updateInfoscreen(infoscreenId, data));
    if (result.success) {
      setInfoscreens(prev => prev.map(i =>
        i.infoscreen_id === infoscreenId ? { ...i, ...result.data } : i
      ));
      if (currentInfoscreen?.infoscreen_id === infoscreenId) {
        setCurrentInfoscreen({ ...currentInfoscreen, ...result.data });
      }
    }
    return result;
  }, [execute, currentInfoscreen]);

  const deleteInfoscreen = useCallback(async (infoscreenId) => {
    const result = await execute(() => infoscreensAPI.deleteInfoscreen(infoscreenId));
    if (result.success) {
      setInfoscreens(prev => prev.filter(i => i.infoscreen_id !== infoscreenId));
    }
    return result;
  }, [execute]);

  const assignRoom = useCallback(async (infoscreenId, roomId) => {
    const result = await execute(() => 
      infoscreensAPI.assignRoom(infoscreenId, { room_id: roomId })
    );
    if (result.success) {
      await fetchInfoscreenById(infoscreenId);
    }
    return result;
  }, [execute, fetchInfoscreenById]);

  const unassignRoom = useCallback(async (infoscreenId, roomId) => {
    const result = await execute(() => 
      infoscreensAPI.unassignRoom(infoscreenId, roomId)
    );
    if (result.success) {
      await fetchInfoscreenById(infoscreenId);
    }
    return result;
  }, [execute, fetchInfoscreenById]);

  const checkInfoscreen = useCallback(async (departmentName, name, loginCode) => {
    const result = await execute(() => 
      infoscreensAPI.checkInfoscreen({
        department_name: departmentName,
        name: name,
        login_code: loginCode
      })
    );
    if (result.success) {
      setCurrentInfoscreen(result.data);
    }
    return result;
  }, [execute]);

  const fetchInfoscreenStatus = useCallback(async (infoscreenId, loginCode) => {
    const result = await execute(() =>
      infoscreensAPI.getInfoscreenStatus(infoscreenId, loginCode)
    );
    if (result.success) {
      setInfoscreenStatus(result.data);
      // The kiosk's login_code is itself a trusted device credential — refresh the
      // same gate token a staff password login would issue, so Switch User from
      // this infoscreen can offer PIN login without ever requiring a password here.
      if (result.data?.switch_session_token) {
        sessionStorage.setItem(SWITCH_SESSION_KEY, result.data.switch_session_token);
      }
    }
    return result;
  }, [execute]);

  return {
    infoscreens,
    currentInfoscreen,
    infoscreenStatus,
    loading,
    error,
    clearError,
    fetchInfoscreens,
    fetchInfoscreenById,
    createInfoscreen,
    updateInfoscreen,
    deleteInfoscreen,
    assignRoom,
    unassignRoom,
    checkInfoscreen,
    fetchInfoscreenStatus,
  };
}